import {
  Card,
  CardContent,
  Field,
  FieldLabel,
  Select,
  Typography,
} from "@atlas/core";
import { Fragment, type ReactNode, useId } from "react";
import { ApplicationGroupMappingBoard } from "./ApplicationGroupMappingBoard";
import { MAPPING_FIGMA_COLORS } from "./applicationGroupMappingUtils";
import { useApplicationGroupMapping } from "./useApplicationGroupMapping";

interface IApplicationGroupMappingProps {
  companyUuid: string | undefined;
  companyName?: string;
}

const MAPPING_STATS = [
  { key: "appTotal", labelKey: "hub.administer.stat_app_groups" },
  { key: "appMapped", labelKey: "hub.administer.stat_app_mapped" },
  { key: "appUnmapped", labelKey: "hub.administer.stat_app_unmapped" },
  { key: "idpTotal", labelKey: "hub.administer.stat_idp_groups" },
  { key: "idpMapped", labelKey: "hub.administer.stat_idp_mapped" },
  { key: "idpUnmapped", labelKey: "hub.administer.stat_idp_unmapped" },
  { key: "activeMappings", labelKey: "hub.administer.stat_active_mappings" },
] as const;

interface IMappingStatCellProps {
  label: string;
  value: number;
}

function MappingStatCell({ label, value }: IMappingStatCellProps) {
  return (
    <section className="flex min-w-0 flex-1 flex-col gap-1 sm:px-2">
      <Typography.Muted className="mt-0 mb-0 text-xs">{label}</Typography.Muted>
      <Typography.H4 className="mt-0 mb-0 text-lg font-bold tabular-nums">
        {value}
      </Typography.H4>
    </section>
  );
}

function MappingPageHint({ children }: { children: ReactNode }) {
  return (
    <Typography.Muted
      className="mt-1 mb-0 text-sm"
      style={{ color: MAPPING_FIGMA_COLORS.hintText }}>
      {children}
    </Typography.Muted>
  );
}

export function ApplicationGroupMapping({
  companyUuid,
  companyName,
}: IApplicationGroupMappingProps) {
  const applicationSelectId = useId();
  const model = useApplicationGroupMapping({
    companyUuid,
    companyName,
  });

  const {
    t,
    companyLabel,
    selectedAppMapId,
    applicationSelectOptions,
    appsLoading,
    handleApplicationSelectChange,
    stats,
  } = model;

  return (
    <section className="flex flex-col gap-4 pb-2 pe-1">
      <header className="flex flex-wrap items-start justify-between gap-4 shrink-0">
        <section className="min-w-0">
          <Typography.H2 className="mt-0 mb-0">
            {t("hub.administer.application_group_mapping_title")}
          </Typography.H2>
          <MappingPageHint>
            {t("hub.administer.application_group_mapping_subtitle", {
              defaultValue: `Connect application groups to IDP groups for ${companyLabel}.`,
              company: companyLabel,
            })}
          </MappingPageHint>
        </section>

        <section className="flex flex-wrap items-end justify-end gap-2">
          <section className="w-64 min-w-[12rem]">
            <Field>
              <FieldLabel htmlFor={applicationSelectId} required>
                {t("hub.administer.application")}
              </FieldLabel>
              <Select
                id={applicationSelectId}
                ariaLabel={t("hub.administer.application")}
                value={selectedAppMapId}
                onChange={handleApplicationSelectChange}
                options={applicationSelectOptions}
                placeholder={t("hub.administer.select_application")}
                disabled={applicationSelectOptions.length === 0 || appsLoading}
                wrapperClassName="max-w-none"
              />
            </Field>
          </section>
        </section>
      </header>

      <Card className="shrink-0 overflow-hidden rounded-[10px] border border-border bg-card shadow-none">
        <CardContent className="flex flex-col gap-4 px-4 py-3 sm:flex-row sm:items-center sm:gap-0 sm:py-4">
          {MAPPING_STATS.map((stat, index) => (
            <Fragment key={stat.key}>
              {index > 0 ? (
                <div
                  className="hidden h-10 w-px shrink-0 bg-border sm:block"
                  aria-hidden
                />
              ) : null}
              <MappingStatCell
                label={t(stat.labelKey)}
                value={stats[stat.key]}
              />
            </Fragment>
          ))}
        </CardContent>
      </Card>

      <ApplicationGroupMappingBoard model={model} />
    </section>
  );
}
